import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { Link } from "react-router-dom";
import { LangToggle, useLang } from "@/lib/i18n";

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    [t.nav.about, "about"],
    [t.nav.industries, "industries"],
    [t.nav.services, "services"],
    [t.nav.process, "process"],
    [t.nav.reach, "global-reach"],
    [t.nav.contact, "contact"],
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || open
          ? "backdrop-blur-xl bg-[#060A14]/80 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <span className="w-8 h-8 bg-gold flex items-center justify-center font-display font-black text-midnight text-lg transition-transform duration-300 group-hover:rotate-90">
            N
          </span>
          <span className="leading-none text-left">
            <span className="block font-display font-800 font-extrabold tracking-tight text-lg text-[#F4F6F8]">
              NOREX
            </span>
            <span className="hidden sm:block text-[9px] uppercase tracking-[0.3em] text-[#94A3B8] whitespace-nowrap">
              International Trading
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="text-sm text-[#94A3B8] hover:text-[#F4F6F8] transition-colors duration-200 tracking-wide"
            >
              {label}
            </button>
          ))}
          <Link
            to="/insights"
            data-testid="nav-link-insights"
            className="text-sm text-[#94A3B8] hover:text-[#F4F6F8] transition-colors duration-200 tracking-wide"
          >
            {t.nav.insights}
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LangToggle />
          <button
            data-testid="nav-rfq-cta"
            onClick={() => go("rfq")}
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-midnight font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
          >
            {t.nav.quote}
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#F4F6F8] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#060A14]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map(([label, id]) => (
                <button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  onClick={() => go(id)}
                  className="text-left text-lg font-display text-[#F4F6F8] py-1"
                >
                  {label}
                </button>
              ))}
              <Link
                to="/insights"
                data-testid="nav-mobile-link-insights"
                onClick={() => setOpen(false)}
                className="text-left text-lg font-display text-[#F4F6F8] py-1"
              >
                {t.nav.insights}
              </Link>
              <button
                data-testid="nav-mobile-rfq-cta"
                onClick={() => go("rfq")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-5 py-3 rounded-full"
              >
                {t.nav.quote} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
